<template>
  <div class="pb-page we">
    <router-link to="/widgets" class="pb-button link pb-back we-back"><i class="mdi mdi-arrow-left" aria-hidden="true"></i> Widgets</router-link>
    <SkeletonList v-if="loading" :rows="3" label="Loading the widget…" />
    <div v-else-if="loadError" class="pb-alert is-error" role="alert"><span>{{ loadError }}</span></div>

    <template v-else-if="widget">
      <PageHeader :title="name || 'Widget'" :subtitle="widgetBrand.name + ' · ' + statusText" wide-only>
        <span v-if="dirty" class="pb-muted">Unsaved changes</span>
        <button type="button" class="pb-button" :disabled="!dirty || saving" @click="save">{{ saving ? 'Saving…' : 'Save changes' }}</button>
      </PageHeader>
      <div v-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span></div>
      <div v-if="widget.refused" class="pb-alert" role="status"><i class="mdi mdi-alert-outline pb-ico" aria-hidden="true"></i><span>{{ widget.refused }}</span></div>

      <section class="we-preview" aria-labelledby="we-prev">
        <div class="pb-row we-prev-top">
          <h2 id="we-prev" class="pb-label we-prev-label">Live preview</h2>
          <div class="pb-segmented" role="group" aria-label="Preview width">
            <button v-for="size in sizes" :key="size" type="button" :aria-pressed="width === size ? 'true' : 'false'" @click="width = size">{{ size.charAt(0).toUpperCase() + size.slice(1) }}</button>
          </div>
        </div>
        <EmbedPreview :widget="widget" :brand="widgetBrand" :width="width" :reload-key="reloadKey" :published="widget.published" />
        <p v-if="dirty" class="pb-muted we-prev-note">Save to see your changes in the preview: it runs the same code your website does.</p>
      </section>

      <!-- Right under the preview: after "Create widget", the code is what people came for. -->
      <section id="install" class="pb-card we-install" aria-labelledby="we-inst">
        <h2 id="we-inst" class="pb-section-title display-font we-h">Install</h2>
        <p v-if="fresh" class="pb-alert is-success we-ready" role="status"><i class="mdi mdi-check-circle-outline pb-ico" aria-hidden="true"></i>
          <span>Your widget is ready: copy this code into your site.</span></p>
        <SnippetBox :value="snippet" label="Embed code" multiline :rows="4" @copied="copied" />
        <InstallGuide class="we-guide" />
        <p v-if="!widget.wall_live" class="pb-note"><i class="mdi mdi-link-variant pb-ico" aria-hidden="true"></i>
          <span>The code's fallback link points to your hosted wall. <router-link to="/wall">Put the wall live</router-link> so that link works too.</span></p>
      </section>
      <div class="we-grid">
        <div class="we-col">
          <section class="pb-card" aria-labelledby="we-layout">
            <h2 id="we-layout" class="pb-section-title display-font we-h">Layout</h2>
            <LayoutPicker :value="layout" :allowed="allowedLayouts" @pick="pickLayout" />
          </section>

          <section class="pb-card" aria-labelledby="we-basic">
            <h2 id="we-basic" class="pb-section-title display-font we-h">Basic</h2>
            <div class="pb-field"><label class="pb-label" for="we-name">Name <span class="optional">(only your team sees it)</span></label>
              <input id="we-name" v-model="name" class="pb-input" maxlength="120"></div>
            <div class="pb-field"><span id="we-theme-l" class="pb-label">Theme</span>
              <div class="pb-segmented" role="radiogroup" aria-labelledby="we-theme-l">
                <button v-for="theme in themes" :key="theme.key" type="button" role="radio" :aria-checked="settings.theme === theme.key ? 'true' : 'false'"
                        :aria-pressed="settings.theme === theme.key ? 'true' : 'false'" @click="settings.theme = theme.key">{{ theme.label }}</button>
              </div>
              <span class="pb-help">Auto follows each visitor's light or dark setting.</span></div>
            <div class="pb-field"><span id="we-accent-l" class="pb-label">Accent colour</span>
              <div class="pb-row we-accent" role="group" aria-labelledby="we-accent-l">
                <label class="pb-check"><input type="radio" name="we-accent" :checked="!settings.accent" @change="settings.accent = null">Brand colour</label>
                <label class="pb-check"><input type="radio" name="we-accent" :checked="!!settings.accent" @change="settings.accent = settings.accent || widgetBrand.color">Custom</label>
                <template v-if="settings.accent">
                  <label class="pb-sr-only" for="we-accent-color">Pick the accent colour</label>
                  <input id="we-accent-color" type="color" :value="settings.accent" class="we-color" @input="settings.accent = $event.target.value.toUpperCase()">
                  <label class="pb-sr-only" for="we-accent-hex">Accent hex code</label>
                  <input id="we-accent-hex" v-model="accentText" class="pb-input we-hex" maxlength="7" @change="applyAccent">
                </template>
              </div></div>
            <label class="pb-check"><input v-model="settings.show_rating" type="checkbox">Show star ratings (per testimonial, never an average)</label>
            <label class="pb-check we-gap"><input v-model="settings.show_photos" type="checkbox">Show customer photos (when the customer allowed it)</label>
            <div class="pb-field we-gap"><label class="pb-label" for="we-max">Testimonials to show: {{ settings.max_items }}</label>
              <input id="we-max" v-model.number="settings.max_items" type="range" min="3" max="24" step="1" class="we-range"></div>
          </section>

          <section class="pb-card" aria-labelledby="we-sel">
            <h2 id="we-sel" class="pb-section-title display-font we-h">Which testimonials</h2>
            <label class="pb-check"><input v-model="selection.mode" type="radio" value="all">All published testimonials (new ones appear automatically)</label>
            <label class="pb-check we-gap"><input v-model="selection.mode" type="radio" value="picked">Only the ones I pick, in this order</label>
            <div v-if="selection.mode === 'picked'" class="we-pick">
              <p v-if="!published.length" class="pb-muted">Publish testimonials first; then pick them here.</p>
              <label v-for="item in published" :key="item.id" class="pb-check we-pick-item">
                <input type="checkbox" :checked="selection.ids.indexOf(item.id) >= 0" @change="togglePick(item.id)">
                <span><span class="we-pick-text">{{ item.excerpt }}</span><span class="pb-muted">{{ item.name || 'Anonymous' }}</span></span>
              </label>
            </div>
          </section>
        </div>
        <div class="we-col">
          <section :class="['pb-card', { 'we-locked': !advanced }]" aria-labelledby="we-adv">
            <div class="pb-card-head"><h2 id="we-adv" class="pb-section-title display-font">Advanced</h2>
              <LockedAction v-if="!advanced" feature="styling" look="pb-button sm tonal" label="Advanced styling" /></div>
            <fieldset :disabled="!advanced" class="we-fieldset">
              <legend class="pb-sr-only">Advanced styling</legend>
              <div class="we-two">
                <div class="pb-field"><label class="pb-label" for="we-font">Font</label>
                  <select id="we-font" v-model="settings.font" class="pb-select"><option value="inherit">Your website's font</option><option value="sans">Clean sans</option><option value="serif">Classic serif</option><option value="rounded">Rounded</option></select></div>
                <div class="pb-field"><label class="pb-label" for="we-card">Card style</label>
                  <select id="we-card" v-model="settings.card_style" class="pb-select"><option value="outline">Outline</option><option value="shadow">Shadow</option><option value="filled">Filled</option></select></div>
                <div class="pb-field"><label class="pb-label" for="we-spacing">Spacing</label>
                  <select id="we-spacing" v-model="settings.spacing" class="pb-select"><option value="compact">Compact</option><option value="normal">Normal</option><option value="roomy">Roomy</option></select></div>
                <div class="pb-field"><label class="pb-label" for="we-columns">Columns</label>
                  <select id="we-columns" v-model.number="settings.columns" class="pb-select"><option :value="0">Automatic</option><option v-for="n in 4" :key="n" :value="n">{{ n }}</option></select></div>
                <div class="pb-field"><label class="pb-label" for="we-order">Order</label>
                  <select id="we-order" v-model="settings.order" class="pb-select"><option value="newest">Newest first</option><option value="oldest">Oldest first</option><option value="rating">Highest rating first</option><option value="manual">Manual order (Testimonials page)</option></select></div>
                <div class="pb-field"><label class="pb-label" for="we-radius">Corner radius: {{ settings.radius }}px</label>
                  <input id="we-radius" v-model.number="settings.radius" type="range" min="0" max="24" class="we-range"></div>
              </div>
              <div class="pb-field"><label class="pb-label" for="we-heading">Heading <span class="optional">(optional)</span></label>
                <input id="we-heading" v-model="settings.heading" class="pb-input" maxlength="80" placeholder="e.g. What our clients say"></div>
              <p class="pb-label">Details to show</p>
              <div class="we-fields">
                <label v-for="field in fieldOptions" :key="field.key" class="pb-check"><input v-model="settings.fields[field.key]" type="checkbox">{{ field.label }}</label>
              </div>
              <p class="pb-help">Imported testimonials always show where they came from.</p>
            </fieldset>
            <label class="pb-check we-brand-toggle"><input type="checkbox" :checked="settings.remove_branding" @change="toggleBranding($event)">Hide "Collected with TrustClap"
              <PlanChip v-if="!limits.remove_branding" :plan="availableOn('remove_branding')" /></label>
          </section>

          <section class="pb-card we-danger" aria-labelledby="we-status">
            <h2 id="we-status" class="pb-section-title display-font we-h">Status</h2>
            <div class="pb-actions">
              <button type="button" class="pb-button outline" @click="toggleStatus">{{ widget.status === 'active' ? 'Pause widget' : 'Turn widget back on' }}</button>
              <template v-if="can('templates')">
                <button v-if="limits.agency_templates" type="button" class="pb-button ghost" @click="saveTemplate">Save as template</button>
                <LockedAction v-else feature="agency_templates" look="pb-button ghost" label="Save as template" />
              </template>
              <button type="button" class="pb-button danger" @click="remove">Delete widget</button>
            </div>
            <p class="pb-help">A paused or deleted widget shows only its fallback link on your site.</p>
          </section>
        </div>
      </div>

    </template>
    <SaveBar :dirty="dirty" :saving="saving" @save="save" />
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import EmbedPreview from '../components/EmbedPreview.vue';
import InstallGuide from '../components/InstallGuide.vue';
import LayoutPicker from '../components/LayoutPicker.vue';
import LockedAction from '../components/LockedAction.vue';
import PageHeader from '../components/PageHeader.vue';
import PlanChip from '../components/PlanChip.vue';
import SaveBar from '../components/SaveBar.vue';
import SkeletonList from '../components/SkeletonList.vue';
import SnippetBox from '../components/SnippetBox.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import workspace from '../mixins/workspace';
import { get, patch, post, remove, message, planLimit } from '../api';
import { normalHex } from '../lib/color';
import { ask } from '../lib/confirm';
import { toast } from '../lib/toast';
import { embedSnippet } from '../publicUrls';

const DEFAULTS = { theme: 'light', accent: null, show_rating: true, show_photos: true, max_items: 9, font: 'inherit', radius: 12, spacing: 'normal',
  card_style: 'outline', columns: 0, order: 'newest', heading: '', remove_branding: false,
  fields: { name: true, title: true, company: true, photo: true, date: false, source: true } };

// The Desktop preview is unreadable on a phone, so narrow screens start with the Phone view.
function narrowScreen() { return typeof window !== 'undefined' && window.innerWidth < 600; }

export default {
  name: 'WidgetEditorView',
  components: { EmbedPreview, InstallGuide, LayoutPicker, LockedAction, PageHeader, PlanChip, SaveBar, SkeletonList, SnippetBox, UpgradeDialog },
  mixins: [workspace],
  data: () => ({ widget: null, name: '', layout: 'grid', settings: JSON.parse(JSON.stringify(DEFAULTS)), selection: { mode: 'all', ids: [] },
    saved: '', loading: true, loadError: '', error: '', saving: false, published: [], width: narrowScreen() ? 'phone' : 'desktop', reloadKey: 0, accentText: '',
    upgrade: null, upgradeOpen: false, sizes: ['desktop', 'tablet', 'phone'],
    themes: [{ key: 'light', label: 'Light' }, { key: 'dark', label: 'Dark' }, { key: 'auto', label: 'Auto' }],
    fieldOptions: [{ key: 'name', label: 'Name' }, { key: 'title', label: 'Job title' }, { key: 'company', label: 'Company' },
      { key: 'photo', label: 'Photo' }, { key: 'date', label: 'Date' }] }),
  computed: {
    widgetBrand() { return this.widget ? (this.ws.brands || []).find(item => item.id === this.widget.brand_id) || this.widget.brand : {}; },
    allowedLayouts() { return (this.widget && this.widget.limits && this.widget.limits.layouts) || ['grid']; },
    advanced() { return !!(this.widget && this.widget.limits && this.widget.limits.styling === 'advanced'); },
    snapshot() { return JSON.stringify({ name: this.name, layout: this.layout, settings: this.settings, selection: this.selection }); },
    dirty() { return !!this.widget && this.snapshot !== this.saved; },
    snippet() { return embedSnippet(this.widget, this.widgetBrand); },
    // As the widgets list labels it: live once a site has shown it, unless the plan or a paused brand keeps it off.
    statusText() {
      if (this.widget.status !== 'active') return 'Paused';
      if (this.widget.refused) return 'Not showing';
      return this.widget.viewed ? 'Live on your site' : 'Active';
    },
    // Just created from the Widgets page.
    fresh() { return this.$route.query.created === '1'; }
  },
  watch: {
    'settings.accent'(value) { this.accentText = value || ''; },
    '$route.params.id'() { this.load(); }
  },
  async mounted() {
    if (!(await this.requireWorkspace())) return;
    await this.load();
  },
  async beforeRouteLeave(to, from, next) {
    if (this.dirty && !(await ask('Leave without saving your changes?', { action: 'Leave' }))) { next(false); return; }
    next();
  },
  methods: {
    take(widget) {
      if (!widget.limits && this.widget && this.widget.limits) widget.limits = this.widget.limits;
      this.widget = widget;
      this.name = widget.name;
      this.layout = widget.layout;
      const settings = Object.assign(JSON.parse(JSON.stringify(DEFAULTS)), widget.settings || {});
      settings.fields = Object.assign({}, DEFAULTS.fields, settings.fields || {});
      this.settings = settings;
      this.selection = { mode: (widget.selection && widget.selection.mode) || 'all', ids: ((widget.selection && widget.selection.ids) || []).slice() };
      this.saved = this.snapshot;
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const widget = await get('/widgets/' + this.$route.params.id);
        this.take(widget);
        this.published = (await get('/brands/' + widget.brand_id + '/submissions', { status: 'published', order: 'manual' })).items || [];
      } catch (error) { this.loadError = message(error, 'This widget no longer exists.'); } finally { this.loading = false; }
    },
    upsell(feature) { this.upgrade = { feature }; this.upgradeOpen = true; },
    pickLayout(item) { if (item.locked) this.upsell('layouts'); else this.layout = item.key; },
    applyAccent() { this.settings.accent = normalHex(this.accentText, this.settings.accent); this.accentText = this.settings.accent; },
    toggleBranding(event) {
      if (event.target.checked && !this.limits.remove_branding && !(this.widget.settings || {}).remove_branding) {
        event.target.checked = false;
        this.upsell('remove_branding');
        return;
      }
      this.settings.remove_branding = event.target.checked;
    },
    togglePick(id) {
      const index = this.selection.ids.indexOf(id);
      if (index >= 0) this.selection.ids.splice(index, 1); else this.selection.ids.push(id);
    },
    fail(error) {
      const limit = planLimit(error);
      if (limit) { this.upgrade = limit; this.upgradeOpen = true; } else this.error = message(error);
    },
    async save() {
      this.saving = true;
      this.error = '';
      try {
        this.take(await patch('/widgets/' + this.widget.id, { name: this.name.trim() || this.widget.name, layout: this.layout,
          settings: this.settings, selection: this.selection }));
        this.reloadKey += 1;
        toast('Saved. Your website shows the change within a minute.');
      } catch (error) { this.fail(error); } finally { this.saving = false; }
    },
    async toggleStatus() {
      try {
        const updated = await patch('/widgets/' + this.widget.id, { status: this.widget.status === 'active' ? 'paused' : 'active' });
        this.widget = Object.assign({}, this.widget, { status: updated.status, viewed: updated.viewed, refused: updated.refused });
        this.reloadKey += 1;
        toast(updated.status !== 'active' ? 'Paused. Your site shows only the fallback link.'
          : (updated.refused || (updated.viewed ? 'The widget is live again.' : 'The widget is active again.')));
        this.refreshWorkspace();
      } catch (error) { this.fail(error); }
    },
    async saveTemplate() {
      try { await post('/templates', { kind: 'widget', name: this.name || this.widget.name, source_id: this.widget.id }); toast('Saved as a template.'); } catch (error) { this.fail(error); }
    },
    async remove() {
      if (!(await ask('Delete this widget? Sites using its code will show only the fallback link.', { action: 'Delete', tone: 'danger' }))) return;
      try {
        await remove('/widgets/' + this.widget.id);
        this.saved = this.snapshot;
        toast('Widget deleted.');
        this.refreshWorkspace();
        this.$router.push('/widgets').catch(() => {});
      } catch (error) { this.fail(error); }
    },
    copied() { toast('Code copied. Paste it into your website.'); }
  }
};
</script>

<style scoped>
.we-back{margin-bottom:16px}
.we-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:20px;align-items:start;margin-top:24px}
.we-col{display:grid;grid-template-columns:minmax(0,1fr);gap:18px;align-content:start}
.we-h{margin-bottom:16px}
.we-gap{margin-top:10px!important}
.we-accent{flex-wrap:wrap;gap:14px}
.we-color{width:44px;height:44px;padding:2px;border:1px solid #8C8378;border-radius:10px;background:#fff;cursor:pointer}
.we-hex{width:110px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace}
.we-range{width:100%;accent-color:#B4472B}
.we-two{display:grid;grid-template-columns:1fr 1fr;gap:0 14px}
.we-fieldset{border:0;margin:0;padding:0;min-width:0}
.we-fieldset:disabled{opacity:.55}
.we-fields{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:8px}
.we-locked{background:#FBF8F3}
.we-brand-toggle{margin-top:16px!important}
.we-pick{margin-top:12px;display:grid;gap:8px;max-height:360px;overflow:auto;padding-right:4px}
.we-pick-item{padding:10px;border:1px solid #E6DED2;border-radius:10px}
.we-pick-item>span{display:flex;flex-direction:column;min-width:0}
.we-pick-text{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.we-preview{min-width:0}
.we-install{margin-top:20px}
.we-ready{margin:0 0 14px}
.we-prev-top{justify-content:space-between;margin-bottom:12px;flex-wrap:wrap}
.we-prev-label{margin:0}
.we-prev-note{margin-top:10px}
.we-guide{margin-top:18px}
@media(max-width:1000px){.we-grid{grid-template-columns:minmax(0,1fr)}}
@media(max-width:600px){.we-two{grid-template-columns:1fr}}
</style>
